import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { TooltipPosition } from '../../types/tutorial';

export interface TutorialPageProps {
  title: string;
  description?: React.ReactNode;
  subtitle?: string;
  children: React.ReactNode;
  currentPage: number;
  totalPages: number;
  onNext?: () => void;
  onPrevious?: () => void;
  onSkip: () => void;
  onFinish?: () => void;
  className?: string;
  cardClassName?: string;
  // Legacy position prop for centered/non-spotlight pages
  position?: 'center' | 'left' | 'right' | 'top';
  // New tooltip-style positioning - when provided, modal positions relative to this element
  targetId?: string;
  // Preferred tooltip position (auto will calculate best position)
  tooltipPosition?: TooltipPosition;
  // Fixed viewport positioning (for multi-spotlight pages) - takes precedence over targetId
  viewportPosition?: 'top' | 'center';
}

interface CalculatedPosition {
  top: number;
  left: number;
  pointerDirection: 'up' | 'down' | 'left' | 'right';
  pointerOffset: number; // offset from edge for pointer
}

const MODAL_GAP = 16; // Gap between modal and spotlight
const MODAL_MIN_WIDTH = 320;
const MODAL_MAX_WIDTH = 672; // max-w-2xl = 42rem = 672px
const VIEWPORT_PADDING = 16;

/**
 * Check if two rectangles overlap
 */
function rectsOverlap(
  rect1: { left: number; top: number; right: number; bottom: number },
  rect2: { left: number; top: number; right: number; bottom: number }
): boolean {
  return !(
    rect1.right < rect2.left ||
    rect1.left > rect2.right ||
    rect1.bottom < rect2.top ||
    rect1.top > rect2.bottom
  );
}

/**
 * Get centered position as fallback
 */
function getCenteredPosition(
  modalWidth: number,
  modalHeight: number,
  viewportWidth: number,
  viewportHeight: number
): CalculatedPosition {
  return {
    top: Math.max(VIEWPORT_PADDING, (viewportHeight - modalHeight) / 2),
    left: Math.max(VIEWPORT_PADDING, (viewportWidth - modalWidth) / 2),
    pointerDirection: 'down', // No pointer for centered
    pointerOffset: 0,
  };
}

/**
 * Calculate the best position for tooltip modal relative to target element
 * Ensures modal always stays within viewport bounds
 */
function calculateTooltipPosition(
  targetRect: DOMRect,
  modalWidth: number,
  modalHeight: number,
  preferredPosition: TooltipPosition = 'auto'
): CalculatedPosition {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Calculate available space on each side of the target
  const spaceTop = targetRect.top - VIEWPORT_PADDING - MODAL_GAP;
  const spaceBottom = viewportHeight - targetRect.bottom - VIEWPORT_PADDING - MODAL_GAP;
  const spaceLeft = targetRect.left - VIEWPORT_PADDING - MODAL_GAP;
  const spaceRight = viewportWidth - targetRect.right - VIEWPORT_PADDING - MODAL_GAP;

  // Target center points
  const targetCenterY = targetRect.top + targetRect.height / 2;
  const targetCenterX = targetRect.left + targetRect.width / 2;

  // Helper to try a specific position and return result if it fits
  const tryPosition = (
    pos: 'right' | 'left' | 'top' | 'bottom'
  ): CalculatedPosition | null => {
    let top = 0;
    let left = 0;
    let pointerDirection: 'up' | 'down' | 'left' | 'right';
    let pointerOffset = 0;

    switch (pos) {
      case 'right':
        left = targetRect.right + MODAL_GAP;
        top = targetCenterY - modalHeight / 2;
        pointerDirection = 'left';
        break;
      case 'left':
        left = targetRect.left - modalWidth - MODAL_GAP;
        top = targetCenterY - modalHeight / 2;
        pointerDirection = 'right';
        break;
      case 'top':
        top = targetRect.top - modalHeight - MODAL_GAP;
        left = targetCenterX - modalWidth / 2;
        pointerDirection = 'down';
        break;
      case 'bottom':
        top = targetRect.bottom + MODAL_GAP;
        left = targetCenterX - modalWidth / 2;
        pointerDirection = 'up';
        break;
    }

    // Clamp to viewport bounds
    const clampedLeft = Math.max(
      VIEWPORT_PADDING,
      Math.min(left, viewportWidth - modalWidth - VIEWPORT_PADDING)
    );
    const clampedTop = Math.max(
      VIEWPORT_PADDING,
      Math.min(top, viewportHeight - modalHeight - VIEWPORT_PADDING)
    );

    // Create modal rect for overlap check
    const modalRect = {
      left: clampedLeft,
      top: clampedTop,
      right: clampedLeft + modalWidth,
      bottom: clampedTop + modalHeight,
    };

    // Target rect for overlap check (with small buffer)
    const targetRectWithBuffer = {
      left: targetRect.left - 4,
      top: targetRect.top - 4,
      right: targetRect.right + 4,
      bottom: targetRect.bottom + 4,
    };

    // Check if modal overlaps with target
    if (rectsOverlap(modalRect, targetRectWithBuffer)) {
      return null; // This position doesn't work
    }

    // Check if modal fits within viewport
    if (
      clampedLeft < VIEWPORT_PADDING ||
      clampedTop < VIEWPORT_PADDING ||
      clampedLeft + modalWidth > viewportWidth - VIEWPORT_PADDING ||
      clampedTop + modalHeight > viewportHeight - VIEWPORT_PADDING
    ) {
      return null; // Doesn't fit
    }

    // Calculate pointer offset based on clamped position
    if (pos === 'right' || pos === 'left') {
      pointerOffset = Math.max(20, Math.min(targetCenterY - clampedTop, modalHeight - 20));
    } else {
      pointerOffset = Math.max(20, Math.min(targetCenterX - clampedLeft, modalWidth - 20));
    }

    return {
      top: clampedTop,
      left: clampedLeft,
      pointerDirection,
      pointerOffset,
    };
  };

  // Build priority list of positions to try
  const positionPriority: Array<'right' | 'left' | 'top' | 'bottom'> = [];

  if (preferredPosition !== 'auto') {
    // Try preferred position first, then others
    positionPriority.push(preferredPosition as 'right' | 'left' | 'top' | 'bottom');
    const others: Array<'right' | 'left' | 'top' | 'bottom'> = ['right', 'left', 'bottom', 'top'];
    others.forEach(p => {
      if (p !== preferredPosition) positionPriority.push(p);
    });
  } else {
    // Auto: prioritize by available space
    const spaces = [
      { pos: 'right' as const, space: spaceRight },
      { pos: 'left' as const, space: spaceLeft },
      { pos: 'bottom' as const, space: spaceBottom },
      { pos: 'top' as const, space: spaceTop },
    ].sort((a, b) => b.space - a.space);

    spaces.forEach(s => positionPriority.push(s.pos));
  }

  // Try each position in priority order
  for (const pos of positionPriority) {
    const result = tryPosition(pos);
    if (result) {
      return result;
    }
  }

  // Fallback: center in viewport (away from target if possible)
  return getCenteredPosition(modalWidth, modalHeight, viewportWidth, viewportHeight);
}

/**
 * Template component for individual tutorial pages
 * Supports both legacy fixed positioning and new tooltip-style dynamic positioning
 */
export const TutorialPage: React.FC<TutorialPageProps> = ({
  title,
  description,
  subtitle,
  children,
  currentPage,
  totalPages,
  onNext,
  onPrevious,
  onSkip,
  onFinish,
  className,
  cardClassName,
  position = 'center',
  targetId,
  tooltipPosition = 'auto',
  viewportPosition
}) => {
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;
  const cardRef = useRef<HTMLDivElement>(null);

  const [calculatedPos, setCalculatedPos] = useState<CalculatedPosition | null>(null);
  const [modalDimensions, setModalDimensions] = useState({ width: MODAL_MAX_WIDTH, height: 400 });

  // Calculate position when targetId is provided
  const updatePosition = useCallback(() => {
    if (!targetId) {
      setCalculatedPos(null);
      return;
    }

    const targetElement = document.querySelector(`[data-tutorial-id="${targetId}"]`);
    if (!targetElement) {
      setCalculatedPos(null);
      return;
    }

    const targetRect = targetElement.getBoundingClientRect();
    const cardWidth = cardRef.current?.offsetWidth || modalDimensions.width;
    const cardHeight = cardRef.current?.offsetHeight || modalDimensions.height;

    const newPos = calculateTooltipPosition(
      targetRect,
      cardWidth,
      cardHeight,
      tooltipPosition
    );

    setCalculatedPos(newPos);
  }, [targetId, tooltipPosition, modalDimensions]);

  // Update modal dimensions after render
  useEffect(() => {
    if (cardRef.current) {
      setModalDimensions({
        width: cardRef.current.offsetWidth,
        height: cardRef.current.offsetHeight
      });
    }
  }, [children, title, subtitle, description]);

  // Initial position calculation and event listeners
  useEffect(() => {
    if (!targetId) return;

    // Initial calculation with a small delay to let elements render
    const initialTimeout = setTimeout(updatePosition, 50);

    // Update on resize and scroll
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      clearTimeout(initialTimeout);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [targetId, updatePosition]);

  // Re-calculate when dimensions change
  useEffect(() => {
    if (targetId) {
      updatePosition();
    }
  }, [modalDimensions, updatePosition, targetId]);

  // Determine positioning mode priority: viewportPosition > targetId (tooltip) > position (legacy)
  const useViewportMode = !!viewportPosition;
  const useTooltipMode = !useViewportMode && targetId && calculatedPos;

  // Pointer/arrow styles based on direction
  const getPointerStyles = () => {
    if (!calculatedPos) return null;

    const { pointerDirection, pointerOffset } = calculatedPos;
    const pointerSize = 12;

    const baseStyles: React.CSSProperties = {
      position: 'absolute',
      width: 0,
      height: 0,
      borderStyle: 'solid',
    };

    switch (pointerDirection) {
      case 'left':
        return {
          ...baseStyles,
          left: -pointerSize,
          top: pointerOffset - pointerSize,
          borderWidth: `${pointerSize}px ${pointerSize}px ${pointerSize}px 0`,
          borderColor: 'transparent var(--card-background, white) transparent transparent',
        };
      case 'right':
        return {
          ...baseStyles,
          right: -pointerSize,
          top: pointerOffset - pointerSize,
          borderWidth: `${pointerSize}px 0 ${pointerSize}px ${pointerSize}px`,
          borderColor: 'transparent transparent transparent var(--card-background, white)',
        };
      case 'up':
        return {
          ...baseStyles,
          top: -pointerSize,
          left: pointerOffset - pointerSize,
          borderWidth: `0 ${pointerSize}px ${pointerSize}px ${pointerSize}px`,
          borderColor: 'transparent transparent var(--card-background, white) transparent',
        };
      case 'down':
        return {
          ...baseStyles,
          bottom: -pointerSize,
          left: pointerOffset - pointerSize,
          borderWidth: `${pointerSize}px ${pointerSize}px 0 ${pointerSize}px`,
          borderColor: 'var(--card-background, white) transparent transparent transparent',
        };
    }
  };

  // Viewport-fixed positioning mode (for multi-spotlight pages)
  if (useViewportMode) {
    return (
      <div
        className={cn(
          'fixed z-[10000] flex p-4',
          viewportPosition === 'top' && 'top-0 left-0 right-0 justify-center pt-4',
          viewportPosition === 'center' && 'inset-0 items-center justify-center',
          className
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tutorial-title"
      >
        {/* Left Chevron - Previous */}
        {!isFirstPage && (
          <button
            onClick={onPrevious}
            className="fixed left-4 top-1/2 -translate-y-1/2 z-[10001] flex flex-col items-center gap-2 px-6 py-8 text-white transition-all duration-200 group"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-12 h-12 group-hover:scale-110 transition-transform" />
            <div className="flex flex-col items-center gap-1">
              <span className="font-semibold text-lg">Previous</span>
              <span className="text-xs text-gray-300">Use left arrow key</span>
            </div>
          </button>
        )}

        {/* Right Chevron - Next/Finish */}
        <button
          onClick={isLastPage ? onFinish : onNext}
          className="fixed right-4 top-1/2 -translate-y-1/2 z-[10001] flex flex-col items-center gap-2 px-6 py-8 text-white transition-all duration-200 group"
          aria-label={isLastPage ? 'Finish tutorial' : 'Next page'}
        >
          <ChevronRight className="w-12 h-12 group-hover:scale-110 transition-transform" />
          <div className="flex flex-col items-center gap-1">
            <span className="font-semibold text-lg">{isLastPage ? 'Finish' : 'Next'}</span>
            <span className="text-xs text-gray-300">Use right arrow key</span>
          </div>
        </button>

        <Card
          ref={cardRef}
          className={cn(
            "w-full max-h-[40vh] overflow-y-auto bg-background border-2 border-blue-500 shadow-2xl animate-in fade-in zoom-in-95 duration-300",
            cardClassName
          )}
        >
          {/* Header */}
          <div className="p-4 pb-3 border-b border-border">
            {/* Progress Indicator */}
            <div className="flex items-center justify-center gap-2 mb-3">
              {Array.from({ length: totalPages }, (_, index) => (
                <div
                  key={index}
                  className={cn(
                    'h-2 rounded-full transition-all duration-300',
                    index + 1 === currentPage
                      ? 'w-8 bg-blue-600'
                      : 'w-2 bg-gray-300 dark:bg-gray-600'
                  )}
                />
              ))}
            </div>

            {/* Page Counter */}
            <p className="text-center text-sm text-muted-foreground mb-2">
              Tutorial {currentPage} / {totalPages}
            </p>

            {/* Title */}
            {title && (
              <h2
                id="tutorial-title"
                className="text-xl font-bold text-center text-foreground mb-1"
              >
                {title}
              </h2>
            )}

            {/* Subtitle */}
            {subtitle && (
              <p className="text-center text-sm text-muted-foreground italic">
                {subtitle}
              </p>
            )}
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Description */}
            {description && (
              <div className="mb-4 text-muted-foreground">
                {description}
              </div>
            )}

            {/* Custom Content */}
            {children}
          </div>

          {/* Footer - Navigation */}
          <div className="p-4 pt-3 border-t border-border flex items-center justify-between gap-4">
            {/* Previous Button */}
            <Button
              variant="outline"
              onClick={onPrevious}
              disabled={isFirstPage}
              className="min-w-[100px]"
            >
              Previous
            </Button>

            {/* Skip/Finish Button */}
            <Button
              variant="outline"
              onClick={onSkip}
              className="text-red-600 border-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:border-red-400 dark:hover:bg-red-950/30 dark:hover:text-red-300"
            >
              Skip Tutorial
            </Button>

            {/* Next/Finish Button */}
            {isLastPage ? (
              <Button
                onClick={onFinish}
                className="min-w-[100px] bg-blue-600 hover:bg-blue-700 text-white"
              >
                Finish
              </Button>
            ) : (
              <Button
                onClick={onNext}
                className="min-w-[100px] bg-blue-600 hover:bg-blue-700 text-white"
              >
                Next
              </Button>
            )}
          </div>
        </Card>
      </div>
    );
  }

  if (useTooltipMode) {
    // Tooltip-style positioning mode
    return (
      <>
        {/* Left Chevron - Previous */}
        {!isFirstPage && (
          <button
            onClick={onPrevious}
            className="fixed left-4 top-1/2 -translate-y-1/2 z-[10001] flex flex-col items-center gap-2 px-6 py-8 text-white transition-all duration-200 group"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-12 h-12 group-hover:scale-110 transition-transform" />
            <div className="flex flex-col items-center gap-1">
              <span className="font-semibold text-lg">Previous</span>
              <span className="text-xs text-gray-300">Use left arrow key</span>
            </div>
          </button>
        )}

        {/* Right Chevron - Next/Finish */}
        <button
          onClick={isLastPage ? onFinish : onNext}
          className="fixed right-4 top-1/2 -translate-y-1/2 z-[10001] flex flex-col items-center gap-2 px-6 py-8 text-white transition-all duration-200 group"
          aria-label={isLastPage ? 'Finish tutorial' : 'Next page'}
        >
          <ChevronRight className="w-12 h-12 group-hover:scale-110 transition-transform" />
          <div className="flex flex-col items-center gap-1">
            <span className="font-semibold text-lg">{isLastPage ? 'Finish' : 'Next'}</span>
            <span className="text-xs text-gray-300">Use right arrow key</span>
          </div>
        </button>

        {/* Tooltip-positioned modal */}
        <div
          className="fixed z-[10000]"
          style={{
            top: calculatedPos.top,
            left: calculatedPos.left,
            maxWidth: MODAL_MAX_WIDTH,
            minWidth: MODAL_MIN_WIDTH,
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="tutorial-title"
        >
          <Card
            ref={cardRef}
            className={cn(
              "relative w-full max-h-[80vh] overflow-y-auto bg-background border-2 border-blue-500 shadow-2xl animate-in fade-in zoom-in-95 duration-300",
              cardClassName
            )}
          >
            {/* Tooltip pointer */}
            <div
              style={getPointerStyles() as React.CSSProperties}
              className="z-[10001]"
            />

            {/* Header */}
            <div className="p-6 pb-4 border-b border-border">
              {/* Progress Indicator */}
              <div className="flex items-center justify-center gap-2 mb-4">
                {Array.from({ length: totalPages }, (_, index) => (
                  <div
                    key={index}
                    className={cn(
                      'h-2 rounded-full transition-all duration-300',
                      index + 1 === currentPage
                        ? 'w-8 bg-blue-600'
                        : 'w-2 bg-gray-300 dark:bg-gray-600'
                    )}
                  />
                ))}
              </div>

              {/* Page Counter */}
              <p className="text-center text-sm text-muted-foreground mb-2">
                Tutorial {currentPage} / {totalPages}
              </p>

              {/* Title */}
              {title && (
                <h2
                  id="tutorial-title"
                  className="text-2xl font-bold text-center text-foreground mb-2"
                >
                  {title}
                </h2>
              )}

              {/* Subtitle */}
              {subtitle && (
                <p className="text-center text-sm text-muted-foreground italic">
                  {subtitle}
                </p>
              )}
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Description */}
              {description && (
                <div className="mb-6 text-muted-foreground">
                  {description}
                </div>
              )}

              {/* Custom Content */}
              {children}
            </div>

            {/* Footer - Navigation */}
            <div className="p-6 pt-4 border-t border-border flex items-center justify-between gap-4">
              {/* Previous Button */}
              <Button
                variant="outline"
                onClick={onPrevious}
                disabled={isFirstPage}
                className="min-w-[100px]"
              >
                Previous
              </Button>

              {/* Skip/Finish Button */}
              <Button
                variant="outline"
                onClick={onSkip}
                className="text-red-600 border-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:border-red-400 dark:hover:bg-red-950/30 dark:hover:text-red-300"
              >
                Skip Tutorial
              </Button>

              {/* Next/Finish Button */}
              {isLastPage ? (
                <Button
                  onClick={onFinish}
                  className="min-w-[100px] bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Finish
                </Button>
              ) : (
                <Button
                  onClick={onNext}
                  className="min-w-[100px] bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Next
                </Button>
              )}
            </div>
          </Card>
        </div>
      </>
    );
  }

  // Legacy fixed position mode (for centered pages like Welcome and Share)
  return (
    <div
      className={cn(
        'fixed inset-0 z-[10000] flex p-4',
        position === 'left' && 'items-center justify-start pl-8',
        position === 'right' && 'items-center justify-end pr-8',
        position === 'center' && 'items-center justify-center',
        position === 'top' && 'items-start justify-center pt-8',
        className
      )}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tutorial-title"
    >
      {/* Left Chevron - Previous */}
      {!isFirstPage && (
        <button
          onClick={onPrevious}
          className="fixed left-4 top-1/2 -translate-y-1/2 z-[10001] flex flex-col items-center gap-2 px-6 py-8 text-white transition-all duration-200 group"
          aria-label="Previous page"
        >
          <ChevronLeft className="w-12 h-12 group-hover:scale-110 transition-transform" />
          <div className="flex flex-col items-center gap-1">
            <span className="font-semibold text-lg">Previous</span>
            <span className="text-xs text-gray-300">Use left arrow key</span>
          </div>
        </button>
      )}

      {/* Right Chevron - Next/Finish */}
      <button
        onClick={isLastPage ? onFinish : onNext}
        className="fixed right-4 top-1/2 -translate-y-1/2 z-[10001] flex flex-col items-center gap-2 px-6 py-8 text-white transition-all duration-200 group"
        aria-label={isLastPage ? 'Finish tutorial' : 'Next page'}
      >
        <ChevronRight className="w-12 h-12 group-hover:scale-110 transition-transform" />
        <div className="flex flex-col items-center gap-1">
          <span className="font-semibold text-lg">{isLastPage ? 'Finish' : 'Next'}</span>
          <span className="text-xs text-gray-300">Use right arrow key</span>
        </div>
      </button>

      <Card
        ref={cardRef}
        className={cn(
          "w-full max-h-[90vh] overflow-y-auto bg-background border-2 border-border shadow-2xl animate-in fade-in zoom-in-95 duration-300",
          position === 'top' ? 'max-w-5xl' : 'max-w-2xl',
          cardClassName
        )}
      >
        {/* Header */}
        <div className="p-6 pb-4 border-b border-border">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center gap-2 mb-4">
            {Array.from({ length: totalPages }, (_, index) => (
              <div
                key={index}
                className={cn(
                  'h-2 rounded-full transition-all duration-300',
                  index + 1 === currentPage
                    ? 'w-8 bg-blue-600'
                    : 'w-2 bg-gray-300 dark:bg-gray-600'
                )}
              />
            ))}
          </div>

          {/* Page Counter */}
          <p className="text-center text-sm text-muted-foreground mb-2">
            Tutorial {currentPage} / {totalPages}
          </p>

          {/* Title */}
          {title && (
            <h2
              id="tutorial-title"
              className="text-2xl font-bold text-center text-foreground mb-2"
            >
              {title}
            </h2>
          )}

          {/* Subtitle */}
          {subtitle && (
            <p className="text-center text-sm text-muted-foreground italic">
              {subtitle}
            </p>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Description */}
          {description && (
            <div className="mb-6 text-muted-foreground">
              {description}
            </div>
          )}

          {/* Custom Content */}
          {children}
        </div>

        {/* Footer - Navigation */}
        <div className="p-6 pt-4 border-t border-border flex items-center justify-between gap-4">
          {/* Previous Button */}
          <Button
            variant="outline"
            onClick={onPrevious}
            disabled={isFirstPage}
            className="min-w-[100px]"
          >
            Previous
          </Button>

          {/* Skip/Finish Button */}
          <Button
            variant="outline"
            onClick={onSkip}
            className="text-red-600 border-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:border-red-400 dark:hover:bg-red-950/30 dark:hover:text-red-300"
          >
            Skip Tutorial
          </Button>

          {/* Next/Finish Button */}
          {isLastPage ? (
            <Button
              onClick={onFinish}
              className="min-w-[100px] bg-blue-600 hover:bg-blue-700 text-white"
            >
              Finish
            </Button>
          ) : (
            <Button
              onClick={onNext}
              className="min-w-[100px] bg-blue-600 hover:bg-blue-700 text-white"
            >
              Next
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default TutorialPage;
